/**
 * Layout-aware text truncation helpers for table cells.
 *
 * Tables in this app use an auto layout, so a single cell holding a long,
 * non-wrapping string stretches its column to fit the text. That pushes the
 * neighboring columns out of view and makes column alignment shift between
 * paginated pages (see VM-3829 for prior per-column fixes).
 *
 * Instead of hand-picking a `max-w-*` Tailwind class for every offending
 * column, these helpers derive a maximum character count from the space a
 * column is given and let the caller truncate the text to fit, appending an
 * ellipsis on overflow. Drop them into any table's column config to solve the
 * layout math once.
 *
 * Usage:
 *   const maxChars = maxCharsPerColumn({ tableWidth: 256, columnCount: 1, padding: 32 });
 *   const label = truncateText(visit.purpose, maxChars); // "To deliver a perfectly craft…"
 */

import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

// Ellipsis appended to truncated text. Single-character glyph (U+2026), not "...".
export const ELLIPSIS = '…';

// Rough average glyph width (px) for DM Sans at the table's base size (16px),
// rounded up to bias toward under-filling. It's only an estimate (the font is
// proportional) and drifts if the font, size, or padding change — but it just
// shifts where we cut the string; the cell's native CSS `truncate` (see TableLink)
// is the real boundary. Re-estimate, or pass a per-column `avgCharWidth`, if the
// type scale changes.
const DEFAULT_AVG_CHAR_WIDTH = 8;

// Never truncate below this many characters, so a very narrow column still
// shows something meaningful instead of a lone ellipsis.
const DEFAULT_MIN_CHARS = 6;

/**
 * Maximum number of characters that fit in a single column.
 *
 * @param {Object} params
 * @param {number} params.tableWidth - Width in px available to the column(s).
 *   Pass the whole table width when columns share it evenly, or a single
 *   column's width with `columnCount: 1`.
 * @param {number} [params.columnCount=1] - How many columns share `tableWidth`.
 * @param {number} [params.avgCharWidth=8] - Estimated px per character.
 * @param {number} [params.minChars=6] - Lower bound on the returned value.
 * @param {number} [params.padding=0] - Horizontal px per column not usable by
 *   text (e.g. cell padding).
 * @returns {number} Max characters per column (always >= minChars).
 */
export const maxCharsPerColumn = ({
  tableWidth,
  columnCount = 1,
  avgCharWidth = DEFAULT_AVG_CHAR_WIDTH,
  minChars = DEFAULT_MIN_CHARS,
  padding = 0,
} = {}) => {
  if (!tableWidth || columnCount < 1 || avgCharWidth <= 0) {
    return minChars;
  }
  const usableWidth = tableWidth / columnCount - padding;
  if (usableWidth <= 0) {
    return minChars;
  }
  return Math.max(minChars, Math.floor(usableWidth / avgCharWidth));
};

/**
 * Truncate text to `maxChars`, appending an ellipsis when it overflows.
 * Null/undefined safe; returns the text unchanged when it already fits.
 *
 * @param {string} text - The full text.
 * @param {number} maxChars - Upper bound on the visible length; the ellipsis
 *   counts toward it, and trailing whitespace is trimmed, so the result may be
 *   a little shorter.
 * @returns {string} The text, truncated with a trailing ellipsis if too long.
 */
export const truncateText = (text, maxChars) => {
  if (text === null || text === undefined) {
    return '';
  }
  const str = String(text);
  if (!Number.isFinite(maxChars) || maxChars <= 0 || str.length <= maxChars) {
    return str;
  }
  // Reserve one character for the ellipsis so the visible length stays ~maxChars.
  return `${str.slice(0, maxChars - 1).trimEnd()}${ELLIPSIS}`;
};

/**
 * Hook that derives `maxChars` from the *rendered* width of a cell instead of a
 * hardcoded one. In our auto-layout, full-width tables a `max-w-*` class on a
 * `<td>` is not enforced, so the column is usually wider than its nominal width
 * on wide screens — a static char cap then truncates with space to spare. This
 * measures the element the returned ref is attached to and recomputes on resize,
 * so the text fills the column and only truncates when it actually runs out of
 * space. Reuses `maxCharsPerColumn` for the math.
 *
 * Attach `measureRef` to a single representative cell in the column (its width
 * is shared by every row); `maxChars` updates as that cell resizes.
 *
 * @param {Object} [params]
 * @param {number} [params.avgCharWidth=8] - Estimated px per character.
 * @param {number} [params.minChars=6] - Lower bound on the returned value.
 * @returns {[Function, number]} `[measureRef, maxChars]`.
 */
export const useColumnMaxChars = ({
  avgCharWidth = DEFAULT_AVG_CHAR_WIDTH,
  minChars = DEFAULT_MIN_CHARS,
} = {}) => {
  const [maxChars, setMaxChars] = useState(minChars);
  const observerRef = useRef(null);

  const measureRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) {
      return;
    }
    // The measured node is the text box itself (padding lives on the wrapping
    // link), so its `clientWidth` is already the space usable by text.
    const update = () => setMaxChars(
      maxCharsPerColumn({ tableWidth: node.clientWidth, avgCharWidth, minChars }),
    );
    update();
    if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(update);
      observerRef.current.observe(node);
    }
  }, [avgCharWidth, minChars]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [measureRef, maxChars];
};

/**
 * Builds a table column `render` that truncates a cell's text to `maxChars` and
 * shows the full value on hover via `title`. Anchors `useColumnMaxChars`'s
 * `measureRef` to the row matching `measuredRowId` (every row shares the column
 * width, so one is enough). Drop into a column config so both the measuring and
 * truncating live in one place.
 *
 * @param {Object} params
 * @param {Function} params.measureRef - The `measureRef` from `useColumnMaxChars`.
 * @param {number} params.maxChars - The `maxChars` from `useColumnMaxChars`.
 * @param {*} params.measuredRowId - `id` of the row to attach `measureRef` to.
 * @returns {(value: *, row: Object) => JSX.Element} A column `render` function.
 */
export const renderTruncatedCell = ({ measureRef, maxChars, measuredRowId }) => (value, row) => {
  const text = value ?? '';
  const truncated = truncateText(text, maxChars);
  // `block truncate` keeps the link from overflowing horizontally, so the shared
  // TableLink hover-tooltip never stringifies this node; `title` shows the full value.
  return (
    <span
      ref={row?.id === measuredRowId ? measureRef : undefined}
      className="block truncate"
      title={truncated === text ? undefined : text}
    >
      {truncated}
    </span>
  );
};
